import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { _T, C, CSS } from '../shared/theme';
import { LANGUAGES, GROUP_LABELS } from '../shared/i18n';

export default function LanguagePicker({ lang, onSelect, onClose }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return q
      ? LANGUAGES.filter(l =>
          l.name.toLowerCase().includes(q) ||
          l.native.toLowerCase().includes(q) ||
          l.code.toLowerCase().includes(q)
        )
      : LANGUAGES;
  }, [query]);

  // Group the filtered results
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(l => {
      if (!map[l.group]) map[l.group] = [];
      // Avoid duplicates (Croatian appears twice in source)
      if (!map[l.group].some(x => x.code === l.code)) map[l.group].push(l);
    });
    return map;
  }, [filtered]);

  const groupOrder = ['popular', 'europe', 'mea', 'asia'];

  return ReactDOM.createPortal(
    <div style={{
      position:'fixed', inset:0, zIndex:1000,
      background:'rgba(0,0,0,0.7)', backdropFilter:'blur(6px)',
      display:'flex', flexDirection:'column',
    }} onClick={onClose}>
      <style>{CSS}</style>
      {/* Sheet */}
      <div
        style={{
          marginTop:'auto',
          background: _T.bg2,
          borderRadius:'20px 20px 0 0',
          border:`1px solid ${_T.cardBorderTop}`,
          maxHeight:'85vh',
          display:'flex', flexDirection:'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding:'18px 20px 12px',
          borderBottom:`1px solid ${_T.cardBorderTop}`,
          display:'flex', alignItems:'center', gap:12,
        }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:9, color:_T.accent, letterSpacing:3, fontWeight:700, marginBottom:4 }}>
              LANGUAGE / LANGUE / SPRACHE
            </div>
            <div style={{ fontFamily:'Georgia,serif', fontSize:18, color:C.white }}>
              Select Language
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width:32, height:32, borderRadius:'50%',
              background:`${C.silver}18`, border:`1px solid ${_T.cardBorderBot}`,
              color:C.silver, fontSize:16, cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}
          >✕</button>
        </div>

        {/* Search */}
        <div style={{ padding:'12px 20px 8px' }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search language…"
            autoFocus
            style={{
              width:'100%', boxSizing:'border-box',
              background:_T.bg3,
              border:`1px solid ${_T.cardBorderTop}`,
              borderRadius:12, padding:'10px 14px',
              color:C.white, fontSize:14,
              fontFamily:'Inter,system-ui,sans-serif',
              outline:'none',
            }}
          />
        </div>

        {/* List */}
        <div style={{ overflowY:'auto', padding:'4px 20px 24px', flex:1 }}>
          {groupOrder.map(group => {
            const items = grouped[group];
            if (!items?.length) return null;
            return (
              <div key={group} style={{ marginBottom:8 }}>
                {/* Group label */}
                <div style={{
                  fontSize:9, color:_T.accent, letterSpacing:3, fontWeight:700,
                  padding:'12px 0 6px',
                }}>
                  {GROUP_LABELS[group]?.toUpperCase()}
                </div>

                {/* Language rows */}
                {items.map(l => {
                  const active = l.code === lang;
                  return (
                    <button
                      key={l.code}
                      onClick={() => { onSelect(l.code); onClose(); }}
                      style={{
                        width:'100%', display:'flex', alignItems:'center', gap:12,
                        padding:'10px 14px', borderRadius:12, marginBottom:3,
                        background: active ? `${_T.accent}18` : 'transparent',
                        border: active
                          ? `1px solid ${_T.accent}55`
                          : '1px solid transparent',
                        cursor:'pointer', textAlign:'left',
                      }}
                    >
                      <span style={{ fontSize:22, lineHeight:1, flexShrink:0, width:28 }}>{l.flag}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{
                          fontSize:14, fontWeight: active ? 700 : 400,
                          color: active ? _T.accent : C.white,
                        }}>
                          {l.native}
                        </div>
                        <div style={{ fontSize:11, color:C.silver, marginTop:1 }}>
                          {l.name}
                          {l.rtl && <span style={{ marginLeft:6, fontSize:9, color:_T.accent }}>RTL</span>}
                        </div>
                      </div>
                      {active && (
                        <div style={{
                          width:18, height:18, borderRadius:'50%',
                          background:_T.accent, flexShrink:0,
                          display:'flex', alignItems:'center', justifyContent:'center',
                          fontSize:10, color:'#0a1220', fontWeight:700,
                        }}>✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div style={{ color:C.silver, textAlign:'center', padding:'40px 0', fontSize:14 }}>
              No languages found
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
